const SERVER_URLS = {
  1: 'https://barkasayal.cclai.in/stream',
  2: 'https://argada.cclai.in/stream',
  3: 'https://nk.cclai.in/stream',
  4: 'https://bk.cclai.in/stream',
  5: 'https://kathara.cclai.in/stream',
  6: 'https://giridih.cclai.in/stream',
  7: 'https://amrapali.cclai.in/stream',
  8: 'https://magadh.cclai.in/stream',
  9: 'https://rajhara.cclai.in/stream',
  10: 'https://kuju.cclai.in/stream',
  11: 'https://hazaribagh.cclai.in/stream',
  12: 'https://rajrappa.cclai.in/stream',
  13: 'https://dhori.cclai.in/stream',
  14: 'https://piparwar.cclai.in/stream'
};


let globalCameraData = {
  camerasData: {},      // cameras by ID
  camerasByIp: {},      // cameras by IP
  camerasByGroupId: {}, // cameras grouped by groupId
};

/**
 * Enhanced coordinate validation and parsing function
 * @param {*} lat - Latitude value
 * @param {*} lng - Longitude value
 * @returns {Object} Object with parsed coordinates and validation status
 */
const validateAndParseCoordinates = (lat, lng) => {
  //console.log('🔍 validateAndParseCoordinates input:', { lat, lng, latType: typeof lat, lngType: typeof lng });
  
  // Helper function to check if value is empty/null/undefined/invalid
  const isEmpty = (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') {
      const trimmed = value.trim().toLowerCase();
      return trimmed === '' || 
             trimmed === 'null' || 
             trimmed === 'undefined' || 
             trimmed === 'n/a' ||
             trimmed === 'nan' ||
             trimmed === 'invalid';
    }
    return false;
  };

  // Helper function to parse coordinate value with strict validation
  const parseCoordinate = (value) => {
    // First check if value is empty/invalid
    if (isEmpty(value)) {
      //console.log('🚫 Empty/invalid value:', value);
      return null;
    }
    
    // If already a valid number
    if (typeof value === 'number') {
      if (isNaN(value) || !isFinite(value)) {
        //console.log('🚫 Invalid number:', value);
        return null;
      }
      return value;
    }
    
    // If string, try to parse
    if (typeof value === 'string') {
      const trimmed = value.trim();
      
      // Check for invalid string values
      if (isEmpty(trimmed)) {
        //console.log('🚫 Invalid string value:', trimmed);
        return null;
      }
      
      // Additional check for string representations of invalid values
      if (trimmed.toLowerCase() === 'undefined' || 
          trimmed.toLowerCase() === 'null' ||
          trimmed.toLowerCase() === 'nan') {
        //console.log('🚫 String representation of invalid value:', trimmed);
        return null;
      }
      
      const parsed = parseFloat(trimmed);
      if (isNaN(parsed) || !isFinite(parsed)) {
        //console.log('🚫 Failed to parse string to number:', trimmed, '-> NaN');
        return null;
      }
      
      //console.log('✅ Successfully parsed string:', trimmed, '->', parsed);
      return parsed;
    }
    
    //console.log('🚫 Unsupported type:', typeof value, value);
    return null;
  };

  // Parse coordinates
  const parsedLat = parseCoordinate(lat);
  const parsedLng = parseCoordinate(lng);
  
  //console.log('📊 Parsed values:', { parsedLat, parsedLng });
  
  // Validate coordinate ranges with more specific checks
  const isValidLat = parsedLat !== null && 
                     typeof parsedLat === 'number' && 
                     !isNaN(parsedLat) && 
                     isFinite(parsedLat) && 
                     parsedLat >= -90 && 
                     parsedLat <= 90;
                     
  const isValidLng = parsedLng !== null && 
                     typeof parsedLng === 'number' && 
                     !isNaN(parsedLng) && 
                     isFinite(parsedLng) && 
                     parsedLng >= -180 && 
                     parsedLng <= 180;
  
  const result = {
    latitude: isValidLat ? Number(parsedLat) : null,
    longitude: isValidLng ? Number(parsedLng) : null,
    isValid: isValidLat && isValidLng,
    originalLat: lat,
    originalLng: lng,
    parsedLat,
    parsedLng,
    isValidLat,
    isValidLng,
    // Additional debugging info
    debugInfo: {
      latEmpty: isEmpty(lat),
      lngEmpty: isEmpty(lng),
      latParsed: parsedLat,
      lngParsed: parsedLng,
      latInRange: parsedLat !== null ? (parsedLat >= -90 && parsedLat <= 90) : false,
      lngInRange: parsedLng !== null ? (parsedLng >= -180 && parsedLng <= 180) : false,
      latType: typeof parsedLat,
      lngType: typeof parsedLng
    }
  };
  
  //console.log('✅ validateAndParseCoordinates result:', result);
  return result;
};

/**
 * Update global camera data (called by WebSocket hook)
 * @param {Array|Object} cameraData - Camera data from WebSocket
 */
export const updateGlobalCameraData = (cameraData) => {
  if (!cameraData) {
    console.warn('⚠️ updateGlobalCameraData: Received null or undefined camera data');
    return;
  }

  

  // Handle different data structures from WebSocket
  let cameras = [];
  
  if (Array.isArray(cameraData)) {
    cameras = cameraData;
  } else if (cameraData.cameras && Array.isArray(cameraData.cameras)) {
    cameras = cameraData.cameras;
  } else if (typeof cameraData === 'object' && cameraData.id && cameraData.ip) {
    cameras = [cameraData];
  } else {
    console.error('❌ updateGlobalCameraData: Invalid camera data structure:', cameraData);
    return;
  }

  if (cameras.length === 0) {
    console.warn('⚠️ updateGlobalCameraData: No cameras to process');
    return;
  }


  // Initialize fresh global data if this is the first update
  const isFirstUpdate = Object.keys(globalCameraData.camerasData || {}).length === 0;
  
  if (isFirstUpdate) {
    globalCameraData = {
      camerasData: {},
      camerasByIp: {},
      camerasByGroupId: {}
    };
  }

  let processedCount = 0;
  let skippedCount = 0;
  let coordsValidCount = 0;

  // Process each camera with enhanced validation
  cameras.forEach((camera, index) => {
    if (!camera || typeof camera !== 'object') {
      console.warn(`⚠️ Invalid camera at index ${index}:`, camera);
      skippedCount++;
      return;
    }

    if (!camera.id || !camera.ip) {
      console.warn(`⚠️ Camera missing required fields at index ${index}:`, {
        id: camera.id,
        ip: camera.ip
      });
      skippedCount++;
      return;
    }

    try {
      // Normalize IP address
      const normalizedIp = camera.ip.toString().trim();
      
      // Convert groupId to number for consistency
      let groupId = camera.groupId;
      const numericGroupId = parseInt(camera.groupId);
      if (!isNaN(numericGroupId)) {
        groupId = numericGroupId;
      }

      
      const coordsResult = validateAndParseCoordinates(camera.latitude, camera.longitude);
      
      if (coordsResult.isValid) {
        coordsValidCount++;
    
      } else {
        console.warn(`⚠️ Invalid coordinates for camera ${camera.ip}:`, {
          originalLat: coordsResult.originalLat,
          originalLng: coordsResult.originalLng,
          parsedLat: coordsResult.parsedLat,
          parsedLng: coordsResult.parsedLng,
          isValidLat: coordsResult.isValidLat,
          isValidLng: coordsResult.isValidLng,
          debugInfo: coordsResult.debugInfo
        });
      }

      // Create enhanced camera object with proper coordinate types
      const processedCamera = {
        ...camera,
        ip: normalizedIp,
        groupId: groupId,
        status: camera.status || 'unknown',
        // Ensure coordinates are proper numbers or null
        latitude: coordsResult.latitude,    // Will be null or a proper number
        longitude: coordsResult.longitude,  // Will be null or a proper number
        hasValidCoords: coordsResult.isValid,
        coordsValidation: coordsResult,
        processedAt: Date.now(),
        originalIp: camera.ip,
        normalizedIp: normalizedIp,
        // Add formatted coordinates for display
        formattedLatitude: coordsResult.latitude !== null ? coordsResult.latitude.toFixed(6) : 'N/A',
        formattedLongitude: coordsResult.longitude !== null ? coordsResult.longitude.toFixed(6) : 'N/A'
      };

      // Store camera data by ID
      globalCameraData.camerasData[camera.id] = processedCamera;

      // Store by normalized IP
      globalCameraData.camerasByIp[normalizedIp] = processedCamera;
      
      // Also store by original IP if different (for backward compatibility)
      if (camera.ip !== normalizedIp) {
        globalCameraData.camerasByIp[camera.ip] = processedCamera;
      }

      // Store by group ID
      if (groupId !== null && groupId !== undefined) {
        if (!globalCameraData.camerasByGroupId[groupId]) {
          globalCameraData.camerasByGroupId[groupId] = [];
        }
        
        // Check if camera already exists in this group (prevent duplicates)
        const existingIndex = globalCameraData.camerasByGroupId[groupId].findIndex(
          existingCamera => existingCamera.id === camera.id
        );
        
        if (existingIndex === -1) {
          globalCameraData.camerasByGroupId[groupId].push(processedCamera);
        } else {
          // Update existing camera in group
          globalCameraData.camerasByGroupId[groupId][existingIndex] = processedCamera;
        }
      }

      processedCount++;
    } catch (error) {
      console.error(`❌ Error processing camera at index ${index}:`, error, camera);
      skippedCount++;
    }
  });

  // Enhanced logging
  console.group('🔄 Global Camera Data Updated');
 


  // Sample camera data for verification
  const sampleCamera = Object.values(globalCameraData.camerasByIp)[0];
  if (sampleCamera) {
 
  }
  
  console.groupEnd();
};

/**
 * Get camera information by IP address with enhanced error handling
 */
export const getCameraByIp = (ip) => {
  if (!ip || ip === 'Unknown') {
    console.warn('⚠️ getCameraByIp: Invalid IP provided:', ip);
    return null;
  }

  //console.log('🔍 Looking up camera for IP:', ip);

  // Try to find the camera in our global data
  const camera = globalCameraData.camerasByIp[ip];
  
  if (!camera) {
    console.warn('❌ Camera not found for IP:', ip);

    return null;
  }


  
  return camera;
};

/**
 * Get camera information by ID
 */
export const getCameraById = (id) => {
  if (!id) {
    console.warn('⚠️ getCameraById: No ID provided');
    return null;
  }
  
  return globalCameraData.camerasData[id] || null;
};

/**
 * Get cameras by group ID
 */
export const getCamerasByGroupId = (groupId) => {
  if (!groupId) {
    console.warn('⚠️ getCamerasByGroupId: No groupId provided');
    return [];
  }
  
  const normalizedGroupId = parseInt(groupId) || groupId;
  return globalCameraData.camerasByGroupId[normalizedGroupId] || [];
};

/**
 * Get video stream URL for a camera
 */
export const getVideoStreamUrl = (cameraIp, groupId) => {
  if (!cameraIp) {
    console.error('❌ getVideoStreamUrl: Missing cameraIp');
    return null;
  }
  
  console.group(`🎥 Generating stream URL for ${cameraIp}, groupId: ${groupId}`);
  
  const camera = getCameraByIp(cameraIp);
  
  if (!camera) {
    console.error(`❌ getVideoStreamUrl: Camera not found for IP ${cameraIp}`);
    console.groupEnd();
    return null;
  }
  
  const actualGroupId = camera.groupId || groupId;
  
  if (!actualGroupId) {
    console.error(`❌ getVideoStreamUrl: No group ID available for camera ${cameraIp}`);
    console.groupEnd();
    return null;
  }
  
  const numericGroupId = parseInt(actualGroupId);
  const serverUrl = SERVER_URLS[numericGroupId];
  
  if (!serverUrl) {
    console.error(`❌ getVideoStreamUrl: No server URL found for group ID ${numericGroupId}`);
    console.groupEnd();
    return null;
  }
  
  // FIXED: Add index.m3u8 to the stream URL for HLS
  const streamUrl = `${serverUrl}/${camera.id}/index.m3u8`;
  
  console.log(`✅ Generated stream URL: ${streamUrl}`);
  console.groupEnd();
  
  return streamUrl;
};

export const getVideoStreamUrlByEvent = (event) => {
  if (!event || !event.cameraIp) {
    console.error('❌ getVideoStreamUrlByEvent: Invalid event or missing cameraIp');
    return null;
  }
    
  const camera = getCameraByIp(event.cameraIp);
  
  if (!camera) {
    console.error('❌ Camera not found for IP:', event.cameraIp);
    return null;
  }

  const groupId = camera.groupId || 
                 event.groupId || 
                 event.originalMessage?.groupId || 
                 event.cameraInfo?.groupId;

  if (!groupId) {
    console.error('❌ No group ID found from any source for camera:', event.cameraIp);
    return null;
  }

  return getVideoStreamUrl(event.cameraIp, groupId);
};

export const isCameraOnline = (cameraIp) => {
  const camera = getCameraByIp(cameraIp);
  return camera ? camera.status === 'online' : false;
};

/**
 * Normalize a string for fuzzy matching: lowercase, strip common noise words
 * (cisf, check, post, gate, entry, etc.) and non-alphanumerics.
 */
const tokenizeForMatch = (str) => {
  if (!str) return [];
  const STOP_WORDS = new Set([
    'cisf', 'check', 'post', 'checkpost', 'gate', 'main', 'no', 'near',
    'the', 'and', '&', 'point', 'view', 'camera', 'cam', 'entry', 'exit', 'in', 'out'
  ]);
  return str
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(t => t.length > 1 && !STOP_WORDS.has(t));
};

/**
 * Returns true if a camera name/location looks like a weighbridge (WB) camera,
 * not a checkpost (CP) camera. Used to filter out wrong matches.
 */
const isWeighbridgeCamera = (camera) => {
  const name = (camera.name || camera.location || '').toUpperCase();
  // Matches "WB 01", "WB01", "WB NO 10", "WB-04", "WB No 21" etc.
  // but NOT "CP 01", "CP01", "CHECKPOST" etc.
  return /\bWB[\s\-]?\d/i.test(name) || /\bWB\s+NO\b/i.test(name);
};

/**
 * Build candidate CP code variants from a raw wbCode like "CP0001".
 * Returns patterns to search for inside camera names, e.g.:
 *   "CP0001" → ["CP0001", "CP001", "CP01", "CP1", "CP 01", "CP 1"]
 */
const cpCodeVariants = (wbCode) => {
  const upper = wbCode.toString().toUpperCase();
  const m = upper.match(/^CP(\d+)$/);
  if (!m) return [upper]; // not a standard CP code — return as-is

  const digits = m[1];                       // e.g. "0001"
  const stripped = parseInt(digits, 10);     // e.g. 1
  const variants = new Set();

  // Exact code first (highest priority)
  variants.add(`CP${digits}`);

  // Without leading zeros — "CP1", "CP5"
  variants.add(`CP${stripped}`);

  // Common 2-digit zero-padded — "CP01", "CP05"
  variants.add(`CP${String(stripped).padStart(2, '0')}`);

  // With space — "CP 01", "CP 1"
  variants.add(`CP ${stripped}`);
  variants.add(`CP ${String(stripped).padStart(2, '0')}`);

  return [...variants];
};

/**
 * Find the camera that best matches a boom-barrier checkpost.
 *
 * Strategy:
 *   1a. Exact wbCode match in camera name (e.g. "CP0711" found verbatim).
 *   1b. Normalized CP code match — try variants like "CP01", "CP 01", "CP1"
 *       so codes like "CP0001" match cameras named "AMP : CP 01".
 *       In both 1a/1b, weighbridge cameras (WB*) are always excluded.
 *   2.  Fuzzy token fallback — but if wbCode starts with "CP", weighbridge
 *       cameras are excluded here too, and a higher score threshold is required.
 *
 * @param {string} checkpostName - boom alert wbName (e.g. "Magadh Checkpost 12 OUT")
 * @param {string} [areaName]    - boom alert areaName (e.g. "Magadh & Sanghmitra")
 * @param {string} [wbCode]      - boom alert wbCode (e.g. "CP0658")
 * @returns {Object|null} matched camera object (with ip/groupId/status) or null
 */
export const getCameraByCheckpost = (checkpostName, areaName = '', wbCode = '') => {
  const allCameras = Object.values(globalCameraData.camerasData || {});
  if (allCameras.length === 0 || !checkpostName) return null;

  const isCheckpostAlert = wbCode && wbCode.toString().toUpperCase().startsWith('CP');

  if (wbCode) {
    const codeUpper = wbCode.toString().toUpperCase();

    // ── Strategy 1a: exact code string present in camera name ───────────────
    const exactMatches = allCameras.filter((camera) => {
      if (isCheckpostAlert && isWeighbridgeCamera(camera)) return false;
      return (camera.name || '').toUpperCase().includes(codeUpper);
    });

    if (exactMatches.length > 0) {
      const online = exactMatches.filter((c) => c.status === 'online');
      return (online.length > 0 ? online : exactMatches)[0];
    }

    // ── Strategy 1b: normalized CP code variants ─────────────────────────────
    if (isCheckpostAlert) {
      const variants = cpCodeVariants(codeUpper); // e.g. ["CP0001","CP01","CP 01","CP1","CP 1"]
      const variantMatches = allCameras.filter((camera) => {
        if (isWeighbridgeCamera(camera)) return false;
        const nameUpper = (camera.name || '').toUpperCase();
        return variants.some((v) => nameUpper.includes(v));
      });

      if (variantMatches.length > 0) {
        const online = variantMatches.filter((c) => c.status === 'online');
        return (online.length > 0 ? online : variantMatches)[0];
      }
    }
  }

  // ── Strategy 2: fuzzy token fallback ────────────────────────────────────────
  const targetTokens = tokenizeForMatch(checkpostName);
  const areaTokens = tokenizeForMatch(areaName);
  if (targetTokens.length === 0) return null;

  let best = null;
  let bestScore = 0;

  allCameras.forEach((camera) => {
    // If this is a checkpost alert, never return a weighbridge camera
    if (isCheckpostAlert && isWeighbridgeCamera(camera)) return;

    const haystack = `${camera.name || ''} ${camera.location || ''}`;
    const camTokens = tokenizeForMatch(haystack);
    if (camTokens.length === 0) return;

    const camSet = new Set(camTokens);
    let score = targetTokens.reduce((acc, t) => acc + (camSet.has(t) ? 2 : 0), 0);

    // Bonus when the camera sits in the same area as the boom
    if (areaTokens.length) {
      const camArea = tokenizeForMatch(camera.area || '');
      const camAreaSet = new Set([...camArea, ...camTokens]);
      if (areaTokens.some(t => camAreaSet.has(t))) score += 1;
    }

    // Bonus for cameras explicitly named as checkpost/CP
    const nameUpper = (camera.name || '').toUpperCase();
    if (/\bCP\s*\d/.test(nameUpper) || /CHECK\s*POST/i.test(nameUpper)) score += 2;

    // Slight bonus for online cameras so we surface a working stream
    if (camera.status === 'online') score += 0.25;

    if (score > bestScore) {
      bestScore = score;
      best = camera;
    }
  });

  // For checkpost alerts require a stronger match (score ≥ 4) to avoid false positives
  const minScore = isCheckpostAlert ? 4 : 2;
  return bestScore >= minScore ? best : null;
};

export const getAllCamerasWithStreams = () => {
  return Object.values(globalCameraData.camerasData).map(camera => ({
    ...camera,
    streamUrl: getVideoStreamUrl(camera.ip, camera.groupId),
    isOnline: camera.status === 'online'
  }));
};

export const getCamerasWithValidCoords = () => {
  return Object.values(globalCameraData.camerasData).filter(camera => camera.hasValidCoords);
};

export const getCameraCoordinates = (cameraIp) => {
  const camera = getCameraByIp(cameraIp);
  
  if (!camera) {
    console.warn('⚠️ getCameraCoordinates: Camera not found for IP:', cameraIp);
    return {
      latitude: null,
      longitude: null,
      hasValidCoords: false,
      formattedLatitude: 'N/A',
      formattedLongitude: 'N/A',
      debugInfo: 'Camera not found'
    };
  }

  return {
    latitude: camera.latitude,           
    longitude: camera.longitude,        
    hasValidCoords: camera.hasValidCoords,
    formattedLatitude: camera.formattedLatitude || 'N/A',
    formattedLongitude: camera.formattedLongitude || 'N/A',
    debugInfo: camera.coordsValidation?.debugInfo || {},
    cameraInfo: {
      id: camera.id,
      ip: camera.ip,
      name: camera.name,
      status: camera.status
    }
  };
};

export const debugCameraData = () => {
  console.group('📹 Camera Data Debug');

  
  const camerasWithCoords = getCamerasWithValidCoords();
  console.groupEnd();
};

export const addCameraData = (camera) => {
  if (!camera || !camera.id || !camera.ip) {
    console.error('❌ addCameraData: Invalid camera data');
    return;
  }
  
  const normalizedIp = camera.ip.trim();
  const groupId = parseInt(camera.groupId) || camera.groupId;
  const coordsResult = validateAndParseCoordinates(camera.latitude, camera.longitude);
  
  const processedCamera = {
    ...camera,
    ip: normalizedIp,
    groupId: groupId,
    latitude: coordsResult.latitude,
    longitude: coordsResult.longitude,
    hasValidCoords: coordsResult.isValid,
    formattedLatitude: coordsResult.latitude !== null ? coordsResult.latitude.toFixed(6) : 'N/A',
    formattedLongitude: coordsResult.longitude !== null ? coordsResult.longitude.toFixed(6) : 'N/A',
    coordsValidation: coordsResult
  };
  
  globalCameraData.camerasData[camera.id] = processedCamera;
  globalCameraData.camerasByIp[normalizedIp] = processedCamera;
  
  if (!globalCameraData.camerasByGroupId[groupId]) {
    globalCameraData.camerasByGroupId[groupId] = [];
  }
  globalCameraData.camerasByGroupId[groupId].push(processedCamera);
  
  //console.log('✅ Camera added manually:', processedCamera);
};

// Export server URLs for external use if needed
export { SERVER_URLS };